import {
  times,
  defaults,
  property,
  noop,
  clamp,
  flowRight as compose,
} from 'lodash';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {mapProps, withHandlers} from 'react-recompose';
import * as React from 'react';

// @ts-expect-error - TS7016 - Could not find a declaration file for module 'rafu'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/rafu/rafu.js' implicitly has an 'any' type.
import {throttle} from 'rafu';
import {relativeCoordinates} from '../logic/domEvents';
import animateProp from './animateProp';
import {
  type TreeViewRoot,
  type SortInfo,
  nodeList,
  nodeDepth,
  getAnchoredPosition,
  isLastChildPosition,
  getNextAnchoredPosition,
  getPrevAnchoredPosition,
  getParent,
  findTreeNode,
  moveNode,
  hasChildren,
  IDSet,
} from './treeViewUtils';

import {translate} from '@packages/utilities/inline-style-utils';
import {snappy, wobbly} from '../constants/Springs';
import {force} from '@packages/utilities/lazy';
import {
  themeable,
  type Theme,
  type PropsWithTheme,
} from '@packages/utilities/styled';
import {resistance} from './PhysicsUtils';
import ScrollView from '../ScrollView';

import * as List from '../List';
import {
  TreeViewArrowRight,
  TreeViewArrowDown,
  TreeViewBranchSpacing,
} from './TreeViewBranchSVG';
import SVGButton from '../SVGButton';

const SORT_START_DELAY = 200;
const DRAG_DISTANCE_THRESHOLD = 2;
const DRAG_RESISTANCE_THRESHOLD = 20;

const caretDown = <TreeViewArrowDown />;
const caretRight = <TreeViewArrowRight />;
const emptyListIcon = <List.Icon />;
// const branchIcon = <BranchIcon />;
const branchCache: Array<any> = [];
const renderBranchIcons = (count: number): Array<any> => {
  if (!branchCache[count]) {
    // @ts-expect-error - TS2769 - No overload matches this call.
    branchCache[count] = <TreeViewBranchSpacing count={count} />;
  }
  return branchCache[count];
};

const resistDisplacement = resistance(DRAG_RESISTANCE_THRESHOLD);

const isLeftButton: (arg1: React.MouseEvent<Element>) => boolean = (event) =>
  event && event.button === 0;

const treeNode = compose(
  themeable,
  animateProp('top', snappy, property('animated')),
  animateProp('blinkOpacity', wobbly, property('blinkOpacity')),
  // @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
  mapProps((props) =>
    defaults(
      {
        style: {
          marginLeft: props.marginLeft,
          borderRadius: props.borderRadius,
          position: 'absolute',
          top: 0,
          left: 0,
          width: props.width,
          pointerEvents: props.animated ? 'none' : undefined,
          opacity: props.sorted ? 0.8 : 1,
          background: props.blinkOpacity
            ? props.theme.TreeList.nodeBackground
            : props.theme.TreeList.background,
          transform: translate(0, props.top),
        },
      },
      props
    )
  ),
  withHandlers({
    onClick:
      ({
        onSelect,
        nodeId,
      }: {
        onSelect: TreeNodeBaseActionProps['onSelect'];
        nodeId: ID;
      }) =>
      () => {
        onSelect(nodeId);
      },
    onDoubleClick:
      ({
        onActivate,
        nodeId,
      }: {
        onActivate?: TreeNodeBaseActionProps['onActivate'];
        nodeId: ID;
      }) =>
      (e: React.MouseEvent) => {
        onActivate?.(nodeId, e);
      },
    onMouseEnter:
      ({
        onPreselect,
        nodeId,
      }: {
        onPreselect: TreeNodeBaseActionProps['onPreselect'];
        nodeId: ID;
      }) =>
      () => {
        onPreselect(nodeId);
      },
    onContextMenu:
      ({
        onContextMenu,
        node,
      }: {
        onContextMenu: (
          arg1: React.MouseEvent<EventTarget>,
          arg2: unknown
        ) => void;
        node: unknown;
      }) =>
      (e: React.MouseEvent<EventTarget>) => {
        onContextMenu(e, node);
      },
    onToggle:
      ({
        onToggle,
        nodeId,
      }: {
        onToggle: TreeNodeBaseActionProps['onToggle'];
        nodeId: ID;
      }) =>
      (event: any) => {
        event.stopPropagation();
        onToggle(nodeId);
      },
  })
);

export type RenderNodeFn<N> = (arg1: {
  node: N;
  depth: number;
  isSelected: boolean;
  isPreselected: boolean;
  renderIndentation: typeof renderBranchIcons;
  expansionButton: React.ReactNode;
  isExpandable: boolean;
  isExpanded: boolean;
  portalTargetRef?: React.RefObject<HTMLElement>;
  top?: number;
  scrollTop?: number;
}) => React.ReactElement;

type ListItemPropsGetter<N> = (arg1: Theme) => (arg1: N) => Omit<
  List.ItemProps,
  'children' | 'style'
> & {
  style?: React.CSSProperties;
  children?: undefined;
  ['data-automation-id']?: string;
};

type TreeNodeBaseActionProps = {
  onActivate?: (id: ID, e: React.MouseEvent) => void;
  onPreselect: (id: ID) => void;
  onSelect: (id: ID) => void;
  onToggle: (id: ID) => void;
  onUnpreselect: (id: ID) => void;
};

type TreeNodeRequiredProps<N> = TreeNodeBaseActionProps & {
  node: N;
  depth: number;
  isPreselected: boolean;
  isSelected: boolean;
  isExpandable: boolean;
  isExpanded: boolean;
  danger?: boolean;
  renderNode: RenderNodeFn<N>;
  // Used in `treeNode` higher-order-component:
  nodeId: ID;
  width: number;
  height: number;
  animated?: boolean;
  sorted?: boolean;
  top?: number;
  blinkOpacity: number;
  onContextMenu: (event: React.MouseEvent<EventTarget>, arg2?: any) => void;
  getListItemProps: ListItemPropsGetter<N>;
  portalTargetRef?: React.RefObject<HTMLElement>;
  scrollTop?: number;
  index?: number;
  borderRadius?: React.CSSProperties['borderRadius'];
  marginLeft?: number;
};

const TreeNode: React.ComponentType<TreeNodeRequiredProps<any>> = treeNode(
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  <N extends unknown>({
    style,
    node,
    depth,
    isPreselected,
    isSelected,
    isExpandable,
    isExpanded,
    danger,
    renderNode,
    onToggle,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onContextMenu,
    onMouseDown,
    onUnpreselect,
    getListItemProps,
    theme,
    portalTargetRef,
    top,
    scrollTop,
    index,
  }: TreeNodeRequiredProps<N> & {
    // Provided by `treeNode` higher-order-component:
    style: React.CSSProperties;
    onContextMenu: (event: React.MouseEvent<EventTarget>, arg2?: any) => void;
    onToggle: () => void;
    onUnpreselect: (arg?: React.MouseEvent<EventTarget>) => void;
    onClick: (arg1?: React.MouseEvent<EventTarget>) => void;
    onDoubleClick: (arg1?: React.MouseEvent<EventTarget>) => void;
    onMouseEnter: (arg1?: React.MouseEvent<EventTarget>) => void;
    onMouseDown: (arg1?: React.MouseEvent<EventTarget>) => void;
    theme: Theme;
  }) => {
    const emptyIcon = depth ? emptyListIcon : null;
    const listItemProps = getListItemProps(theme)(node);

    const expansionButton = isExpandable ? (
      <List.Icon>
        <SVGButton
          style={{color: listItemProps?.style?.color}}
          onClick={onToggle}
          icon={isExpanded ? caretDown : caretRight}
          data-automation-id="navigator-expand-collapse-caret-button"
          allowGlobalEventHandlers
        />
      </List.Icon>
    ) : (
      emptyIcon
    );

    return (
      <List.Item
        style={style}
        depth={depth}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        onMouseDown={onMouseDown}
        onContextMenu={onContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={(event) => {
          onUnpreselect(event);
        }}
        hover={isPreselected}
        active={isSelected}
        danger={danger}
        data-hovered={isPreselected ? true : undefined}
        data-selected={isSelected ? true : undefined}
        data-node-list-index={index}
        {...listItemProps}
      >
        {renderNode({
          node,
          depth,
          isSelected,
          isPreselected,
          renderIndentation: renderBranchIcons,
          expansionButton,
          isExpanded,
          isExpandable,
          portalTargetRef,
          scrollTop,
          top,
        })}
      </List.Item>
    );
  }
);

const elasticDisplacement = (
  pointerY: number,
  scrollTop: number,
  selectedIndex: number,
  nodeHeight: number
) => {
  let displacement = pointerY + scrollTop - selectedIndex * nodeHeight;

  if (displacement < nodeHeight && displacement > 0) {
    displacement = 0;
  } else {
    displacement += displacement > 0 ? -nodeHeight : 0;
  }

  displacement =
    (displacement > 0 ? 1 : -1) * resistDisplacement(Math.abs(displacement));

  return displacement;
};

const getBounds = (
  scrollTop: number,
  height: number,
  nodeHeight: number,
  nodeCount: number
) => {
  const begin = scrollTop > 0 ? Math.floor(scrollTop / nodeHeight) : 0;
  const end = Math.min(nodeCount, begin + Math.ceil(height / nodeHeight) + 1);

  return {begin, end};
};

type TreeNodesProps<K, N> = TreeNodeBaseActionProps & {
  begin: number;
  end: number;
  expandedNodes: Partial<Record<ID, boolean>>;
  invalidSort: undefined | boolean;
  nodeHeight: number;
  onContextMenu: (event: React.MouseEvent<EventTarget>, arg2: N) => void;
  pointerMoveCoordinates: null | Coordinate;
  potentialParent: string | null | undefined;
  preselectedId: string | null | undefined;
  renderNode: RenderNodeFn<N>;
  root: TreeViewRoot<K, N>;
  selectedIds: IDSet;
  sorting: undefined | boolean;
  width: number;
  getListItemProps: ListItemPropsGetter<N>;
  automationId?: string;
  portalTargetRef?: React.RefObject<HTMLElement>;
  top?: number;
  scrollTop: number;
  borderRadius?: React.CSSProperties['borderRadius'];
  marginLeft?: number;
};

/**
 * Represents additional data about the currently selected node(s)
 */
type SelectedNodeData = {
  /**
   * The TreeView id of this selected node
   */
  selectedId: ID;
  /**
   * The parent id of this selected node
   */
  parentId: ID | null;
  /**
   * The index where this node can be found in the list produced by calling nodeList()
   */
  listIndex: number;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const TreeNodes_ = <K extends unknown, N extends unknown>({
  width,
  begin,
  end,
  nodeHeight,
  scrollTop,
  root,
  expandedNodes,
  selectedIds,
  preselectedId,
  sorting,
  invalidSort,
  potentialParent,
  pointerMoveCoordinates,
  renderNode,
  onContextMenu,
  onPreselect,
  onUnpreselect,
  onSelect,
  onActivate,
  onToggle,
  getListItemProps,
  automationId,
  portalTargetRef,
  borderRadius,
  marginLeft,
}: TreeNodesProps<K, N>) => {
  const list = nodeList(expandedNodes, root);

  // Collects data about the currently selected nodes in the shape of `SelectedNodeData`
  const selectedNodes = Array.from(selectedIds).reduce<SelectedNodeData[]>(
    (res, selectedId) => {
      const foundTreeNode = selectedId
        ? findTreeNode(root, selectedId, expandedNodes)
        : null;

      const selectedIndex =
        selectedId && foundTreeNode ? list.indexOf(foundTreeNode) : -1;

      if (selectedIndex === -1) return res;

      const parentTreeNode = getParent(root, selectedId, expandedNodes);

      const parent = parentTreeNode ? parentTreeNode[0] : null;
      const parentId = parent ? root.getId(parent) : null;

      res.push({
        selectedId,
        parentId,
        listIndex: selectedIndex,
      });

      return res;
    },
    []
  );

  const haveSelectedNode = selectedNodes.length > 0;

  // NOTE: This handles highlighting the parent element when a sort/dragging of an element is active.
  // It is kept mostly for backwards compatibility for single-item selection in the Navigator.
  // If multiple items are selected during a drag, the parent highlight will not show.
  const preselectedNodeDuringSort =
    sorting && selectedNodes.length === 1 ? selectedNodes[0]?.parentId : null;

  const nodeRenderer = (i: number) => {
    const index = i + begin;
    const [node, lazyChildren] = list[index]!;
    const children = force(lazyChildren);
    const id = root.getId(node);
    const selected = selectedIds.has(id);
    const preselected = preselectedId === id;

    // Handle rendering of the selected node separately.
    if (selected) {
      return null;
    }
    // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
    const depth = nodeDepth(expandedNodes)(root)(node);
    const top = Math.round(index * nodeHeight);

    return (
      <TreeNode
        key={id}
        nodeId={id}
        top={top}
        blinkOpacity={potentialParent === id ? 1 : 0}
        width={width}
        height={nodeHeight}
        animated={sorting}
        node={node}
        depth={depth}
        isPreselected={sorting ? preselectedNodeDuringSort === id : preselected}
        isSelected={false}
        isExpandable={Boolean(
          hasChildren(children) || (expandedNodes[id] && sorting)
        )}
        isExpanded={Boolean(expandedNodes[id])}
        renderNode={renderNode}
        onToggle={onToggle}
        onSelect={onSelect}
        onActivate={onActivate}
        onPreselect={!sorting ? onPreselect : noop}
        onUnpreselect={onUnpreselect}
        getListItemProps={getListItemProps}
        onContextMenu={onContextMenu}
        portalTargetRef={preselected ? portalTargetRef : undefined}
        scrollTop={preselected ? scrollTop : undefined}
        index={index}
        borderRadius={borderRadius}
        marginLeft={marginLeft}
      />
    );
  };

  // Need to handle selected node separately because it might not be in the
  // index range that is in view and it needs to display on top of other nodes.
  const selectedNodeRenderer = () => {
    return selectedNodes.map((selectedNode) => {
      const [node, lazyChildren] = list[selectedNode.listIndex]!;
      const children = force(lazyChildren);
      const id = root.getId(node);
      const depth = nodeDepth<K, N>(expandedNodes)(root)(node);
      const preselected = preselectedId === id;

      const top =
        Math.round(selectedNode.listIndex * nodeHeight) +
        (sorting && pointerMoveCoordinates
          ? elasticDisplacement(
              pointerMoveCoordinates.y,
              scrollTop,
              selectedNode.listIndex,
              nodeHeight
            )
          : 0);

      return (
        <TreeNode
          key={id}
          nodeId={id}
          top={top}
          blinkOpacity={0}
          width={width}
          height={nodeHeight}
          animated={sorting}
          sorted={sorting}
          node={node}
          depth={depth}
          danger={invalidSort}
          isPreselected={
            sorting ? preselectedNodeDuringSort === id : preselected
          }
          isSelected
          isExpandable={Boolean(hasChildren(children))}
          isExpanded={Boolean(expandedNodes[id])}
          renderNode={renderNode}
          onToggle={onToggle}
          onSelect={onSelect}
          onActivate={onActivate}
          onPreselect={onPreselect}
          onUnpreselect={onUnpreselect}
          getListItemProps={getListItemProps}
          onContextMenu={onContextMenu}
          portalTargetRef={preselected ? portalTargetRef : undefined}
          scrollTop={preselected ? scrollTop : undefined}
          index={selectedNode.listIndex}
          borderRadius={borderRadius}
          marginLeft={marginLeft}
        />
      );
    });
  };

  const children = times(end - begin, nodeRenderer);

  return (
    <div data-automation-id={automationId}>
      {haveSelectedNode ? children.concat(selectedNodeRenderer()) : children}
    </div>
  );
};

const TreeNodes: typeof TreeNodes_ = React.memo(TreeNodes_) as any;

type TreeViewProps<K, N> = Props<K, N> &
  State & {
    nodeWidth: number;
    onMouseDown: (arg1: React.MouseEvent<Element>) => void;
    onMouseMove: (arg1: React.MouseEvent<Element>) => void;
    onMouseLeave: (arg1: React.MouseEvent<Element>) => void;
    onMouseUp: (arg1: React.MouseEvent<Element>) => void;
    onDragOver: (arg1: React.MouseEvent<Element>) => void;
    onContextMenu: (event: React.MouseEvent<Element>, arg2?: N) => void;
  };

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const TreeView = <K extends unknown, N extends unknown>(
  treeViewProps: TreeViewProps<K, N>
) => {
  const {
    width,
    height,
    nodeWidth,
    nodeHeight,
    scrollView,
    root,
    expandedNodes,
    selectedNodes,
    preselectedNode,
    sorting,
    sort,
    invalidSort,
    potentialParent,
    pointerMoveCoordinates,
    renderNode,

    onPreselect,
    onUnpreselect,
    onSelect,
    onActivate,
    onToggle,
    onScroll,

    onMouseDown,
    onMouseMove,
    onMouseLeave,
    onMouseUp,
    onDragOver,
    onContextMenu,
    getListItemProps,
    showShadows,
    automationId,
    borderRadius,
    marginLeft,
  } = treeViewProps;
  const {scrollTop} = scrollView;
  const scrollLeft = clamp(0, scrollView.scrollLeft || 0, nodeWidth - width);
  const list = nodeList(expandedNodes, root);
  const {begin, end} = getBounds(
    scrollTop || 0,
    height,
    nodeHeight,
    list.length
  );

  // If we are sorting consider the sortNode selected.
  const selectedIds = React.useMemo(() => {
    return sort && sort.nodeId ? IDSet.from([sort.nodeId]) : selectedNodes;
  }, [selectedNodes, sort]);

  const hasScrollTop = typeof scrollTop === 'number';

  const portalTargetRef = React.useRef<HTMLDivElement>(null);

  const scrollTopToPass = React.useMemo(() => {
    if (!hasScrollTop) return 0;

    if (pointerMoveCoordinates) {
      return scrollTop;
    }

    if (sorting && pointerMoveCoordinates) {
      return scrollTop;
    }

    return 0;
  }, [sorting, pointerMoveCoordinates, hasScrollTop, scrollTop]);

  const treeNodesProps: TreeNodesProps<K, N> = {
    width: nodeWidth,
    begin,
    end,
    nodeHeight,
    sorting,
    scrollTop: scrollTopToPass,
    root,
    expandedNodes,
    selectedIds,
    preselectedId: preselectedNode,
    invalidSort,
    potentialParent,
    pointerMoveCoordinates,
    renderNode,
    onPreselect,
    onUnpreselect,
    onSelect,
    onActivate,
    onToggle,
    getListItemProps,
    automationId,
    onContextMenu,
    portalTargetRef,
    borderRadius,
    marginLeft,
  };

  const transformScrollTop = hasScrollTop ? -scrollTop : 0;

  return (
    // TODO: fix jsx-a11y lint error
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      style={{
        cursor: sorting ? '-webkit-grabbing' : undefined,
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onDragOver={onDragOver}
      onDrop={onMouseUp}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      data-legacy-ds="TreeView"
    >
      <ScrollView
        width={width}
        height={height}
        scrollWidth={nodeWidth}
        scrollHeight={list.length * nodeHeight}
        scrollTop={scrollTop}
        scrollLeft={scrollLeft}
        onScroll={onScroll}
        showShadows={showShadows}
      >
        <div
          data-automation-id="tree-view-container"
          ref={portalTargetRef}
          style={{width: '100%', height: '100%'}}
        >
          <div
            style={{
              transform: translate(-scrollLeft, transformScrollTop),
            }}
          >
            <TreeNodes {...treeNodesProps} />
          </div>
        </div>
      </ScrollView>
    </div>
  );
};

type ID = string;

export type Props<K, N> = TreeNodeBaseActionProps & {
  width: number;
  height: number;
  nodeWidth: number;
  nodeHeight: number;
  root: TreeViewRoot<K, N>;
  selectedNodes: IDSet;
  preselectedNode?: string | null | undefined;
  sorting?: boolean;
  sort?: null | SortInfo;
  potentialParent?: string | null | undefined;
  invalidSort?: boolean;
  scrollView: {
    scrollTop?: number;
    scrollLeft?: number;
  };
  expandedNodes: Partial<Record<ID, boolean>>;
  renderNode: RenderNodeFn<N>;
  onSortStart: (arg1: SortInfo) => void;
  onSortChange: (arg1: SortInfo) => void;
  onSortEnd: (arg1: SortInfo) => void;
  onPotentialParentHover: (arg1: null | ID) => void;
  onScroll: (arg1: {scrollTop?: number; scrollLeft?: number}) => void;
  onContextMenu: (event: React.MouseEvent<EventTarget>, arg2?: any) => void;
  getListItemProps: ListItemPropsGetter<N>;
  showShadows?: boolean;
  automationId?: string;
  borderRadius?: React.CSSProperties['borderRadius'];
  marginLeft?: number;
};

type ThemedProps<K, N> = PropsWithTheme<Props<K, N>>;

type Coordinate = {
  x: number;
  y: number;
};

type State = {
  pointerDownCoordinates: null | Coordinate;
  pointerMoveCoordinates: null | Coordinate;
  verticalDisplacement: number;
};

const initialState: State = {
  pointerDownCoordinates: null,
  pointerMoveCoordinates: null,
  verticalDisplacement: 0,
};

const TreeViewWithState = themeable(
  class TreeViewWithState_<K, N> extends React.Component<
    ThemedProps<K, N>,
    State
  > {
    state: State = initialState;
    sortStartTimer: ReturnType<typeof setTimeout> | undefined = undefined;

    getOffsetAtCoordinates = (coordinates: Coordinate) => {
      const scrollTop = this.props.scrollView.scrollTop || 0;
      return (coordinates.y + scrollTop) / this.props.nodeHeight;
    };

    // @ts-expect-error - TS2322 - Type '() => TreeViewRoot<unknown, unknown> | TreeViewRoot<K, N>' is not assignable to type '() => TreeViewRoot<K, N>'.
    getRoot: () => TreeViewRoot<K, N> = () => {
      const {expandedNodes, root, sort} = this.props;
      // @ts-expect-error - TS2345 - Argument of type 'TreeViewRoot<K, N>' is not assignable to parameter of type 'TreeViewRoot<unknown, unknown>'.
      return sort ? moveNode(expandedNodes)(sort)(root) : root;
    };

    getNodeAtCoordinates: (arg1: Coordinate) => undefined | N = (
      coordinates
    ) => {
      const {expandedNodes} = this.props;
      const list = nodeList(expandedNodes, this.getRoot());
      const indexAtCoordinates = Math.floor(
        this.getOffsetAtCoordinates(coordinates)
      );
      const treeViewNode = list[indexAtCoordinates];
      return treeViewNode ? treeViewNode[0] : undefined;
    };

    onMouseDown = (event: React.MouseEvent<Element>) => {
      if (!isLeftButton(event)) {
        return;
      }

      const coordinates: Coordinate = relativeCoordinates(event);
      const nodeAtPointer = this.getNodeAtCoordinates(coordinates);

      if (
        // If a node exists at our pointer coordinates...
        nodeAtPointer &&
        // ...and that node is not one of our root nodes...
        this.getRoot().nodes.every(
          // @ts-expect-error - TS2769 - No overload matches this call.
          ([rootNode]: [any]) => nodeAtPointer !== rootNode
        )
      ) {
        this.sortStartTimer = setTimeout(() => {
          const root = this.getRoot();
          const {expandedNodes} = this.props;
          // Get node again in case this.props have changed in the mean time.
          const node = this.getNodeAtCoordinates(coordinates);

          this.setState({pointerDownCoordinates: null});

          if (node) {
            this.props.onSortStart(
              getAnchoredPosition(root.getId(node), expandedNodes, root)
            );
          }
        }, SORT_START_DELAY);
      }

      this.setState({pointerDownCoordinates: coordinates});
    };

    // Throttling this because it is called multiple times per frame when
    // the native `dragover` event fires.
    handleMouseMove = throttle((coordinates: Coordinate) => {
      const {
        pointerDownCoordinates,
        pointerMoveCoordinates,
        verticalDisplacement,
      } = this.state;
      const {
        theme,
        expandedNodes,
        sort,
        sorting,
        potentialParent,
        onPotentialParentHover,
        onSortStart,
        onSortChange,
      } = this.props;
      const root = this.getRoot();

      let newVerticalDisplacement = verticalDisplacement;
      let newPotentialParent = null;

      if (sorting && sort) {
        const sortNodeId = sort.nodeId;
        const offsetAtPointer = this.getOffsetAtCoordinates(coordinates);
        const list = nodeList(expandedNodes, root);

        const indexAtPointer = clamp(
          Math.floor(offsetAtPointer),
          1,
          list.length - 1
        );

        const selectedIndex = list.indexOf(
          // @ts-expect-error - TS2345 - Argument of type 'TreeViewNode<K, N> | null' is not assignable to parameter of type 'TreeViewNode<K, N>'.
          findTreeNode(root, sortNodeId, expandedNodes)
        );
        const offsetDiff = offsetAtPointer - indexAtPointer;

        if (indexAtPointer === selectedIndex) {
          // Handle last node in subtree case
          if (isLastChildPosition(sortNodeId, expandedNodes, root)) {
            const displacement = pointerMoveCoordinates
              ? coordinates.x - pointerMoveCoordinates.x + verticalDisplacement
              : 0;

            const depthPadding = theme.list.padding;
            const remainingDistance = displacement % depthPadding;
            const levelsMoved =
              (displacement - remainingDistance) / depthPadding;

            if (levelsMoved > 0) {
              const nextAnchoredPosition = getNextAnchoredPosition(
                sortNodeId,
                expandedNodes,
                root
              );

              if (nextAnchoredPosition) {
                onSortChange(nextAnchoredPosition);
              }
            } else if (levelsMoved < 0) {
              const prevAnchoredPosition = getPrevAnchoredPosition(
                sortNodeId,
                expandedNodes,
                root
              );

              if (prevAnchoredPosition) {
                onSortChange(prevAnchoredPosition);
              }
            }

            newVerticalDisplacement = remainingDistance;
          }
        } else {
          // Only actually change sort index if the pointer traveled more than
          // 50% of the node height; otherwise the user might just want to expand
          // the node underneath.
          const nextSelectedIndex =
            indexAtPointer > selectedIndex
              ? offsetDiff > 0.5
                ? indexAtPointer
                : indexAtPointer - 1
              : offsetDiff < 0.5
                ? indexAtPointer
                : indexAtPointer + 1;

          if (indexAtPointer !== nextSelectedIndex) {
            const [nodeAtPointer] = list[indexAtPointer]!;

            if (nodeAtPointer) {
              newPotentialParent = root.getId(nodeAtPointer);
            }
          }

          if (nextSelectedIndex !== selectedIndex) {
            const [nextAnchorNode] = list[nextSelectedIndex]!;
            const nextAnchorId = root.getId(nextAnchorNode);
            const nextPosition =
              nextSelectedIndex > selectedIndex
                ? expandedNodes[nextAnchorId]
                  ? 'prepend'
                  : 'after'
                : 'before';

            onSortChange({
              nodeId: sortNodeId,
              anchorId: nextAnchorId,
              position: nextPosition,
            });
          }
        }
      } else if (
        pointerDownCoordinates &&
        Math.abs(coordinates.y - pointerDownCoordinates.y) >
          DRAG_DISTANCE_THRESHOLD
      ) {
        const node = this.getNodeAtCoordinates(pointerDownCoordinates);
        clearTimeout(this.sortStartTimer);

        // Remove pointerDownCoordinates otherwise if sorting ends outside
        // the treeview, this code will cause sorting to start again.
        this.setState({pointerDownCoordinates: null});

        if (
          // If a node exists at our pointer coordinates...
          node &&
          // ...and that node is not one of our root nodes...
          // @ts-expect-error - TS2769 - No overload matches this call.
          root.nodes.every(([rootNode]: [any]) => node !== rootNode)
        ) {
          onSortStart(
            getAnchoredPosition(root.getId(node), expandedNodes, root)
          );
        }
      }

      this.setState({
        verticalDisplacement: newVerticalDisplacement,
        pointerMoveCoordinates: coordinates,
      });

      if (newPotentialParent !== potentialParent) {
        onPotentialParentHover(newPotentialParent);
      }
    });

    onMouseMove = (event: React.MouseEvent<Element>) => {
      this.handleMouseMove(relativeCoordinates(event));
    };

    onDragOver = (event: React.MouseEvent<Element>) => {
      this.handleMouseMove(relativeCoordinates(event));
    };

    onMouseLeave = () => {
      this.setState({
        pointerMoveCoordinates: null,
      });
    };

    onMouseUp = () => {
      const {sorting, onSortEnd, sort} = this.props;

      clearTimeout(this.sortStartTimer);

      if (sorting && sort) {
        onSortEnd(sort);
      }

      this.setState({
        pointerDownCoordinates: null,
      });
    };

    componentWillUnmount() {
      if (this.handleMouseMove && this.handleMouseMove.cancel) {
        this.handleMouseMove.cancel();
      }
      clearTimeout(this.sortStartTimer);
    }

    render() {
      return (
        <TreeView
          {...defaults(
            {
              onMouseDown: this.onMouseDown,
              onMouseMove: this.onMouseMove,
              onMouseLeave: this.onMouseLeave,
              onMouseUp: this.onMouseUp,
              onDragOver: this.onDragOver,
              root: this.getRoot(),
            },
            this.state,
            this.props
          )}
        />
      );
    }
  }
);

TreeViewWithState.displayName = 'TreeView';

class TreeViewT<K, N> extends React.Component<Props<K, N>> {}

export default TreeViewWithState as typeof TreeViewT;
